import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../utils/api';
import './WebhookSettings.css';

const TYPE_OPTIONS = [
  { value: 'generic', label: '🔗 Generic (JSON)', desc: 'Raw JSON payload' },
  { value: 'slack', label: '💬 Slack', desc: 'Formatted Block Kit message' },
  { value: 'teams', label: '🟦 Microsoft Teams', desc: 'Adaptive Card / MessageCard' },
  { value: 'email', label: '📧 Email', desc: 'SMTP email alert' },
];

const EVENT_OPTIONS = [
  { value: 'sbom_uploaded', label: 'SBOM Uploaded', desc: 'New SBOM file processed' },
  { value: 'policy_failed', label: 'Policy Failed', desc: 'CI/CD policy gate failed' },
  { value: 'new_vulnerability', label: 'New Vulnerability', desc: 'CVE found in SBOM scan' },
  { value: 'overdue_remediation', label: 'Overdue Remediation', desc: 'Deadline passed for a fix' },
];

const SEVERITY_OPTIONS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

const typeIcon = (type) => ({ slack: '💬', teams: '🟦', generic: '🔗', email: '📧' }[type] || '🔗');

const statusBadge = (status) => {
  if (!status) return null;
  if (status >= 200 && status < 300)
    return <span className="badge-status-ok">✓ {status}</span>;
  return <span className="badge-status-err">✗ {status}</span>;
};

function emptyForm() {
  return {
    name: '',
    url: '',
    secret: '',
    type: 'generic',
    events: ['new_vulnerability'],
    minSeverity: 'HIGH',
    repos: [],
    active: true,
  };
}

export default function WebhookSettings() {
  const [hooks, setHooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, id = edit
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [testMsg, setTestMsg] = useState({});
  const [deliveries, setDeliveries] = useState({});
  const [expandedDeliveries, setExpandedDeliveries] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [smtp, setSmtp] = useState({ host: '', port: '587', user: '', pass: '', from: '' });
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpMsg, setSmtpMsg] = useState('');

  const loadHooks = useCallback(async () => {
    try {
      const res = await authFetch('/webhooks');
      const data = await res.json();
      setHooks(Array.isArray(data) ? data : []);
    } catch (_) {
      setHooks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSmtp = useCallback(async () => {
    try {
      const res = await authFetch('/settings/smtp');
      if (res.ok) {
        const data = await res.json();
        setSmtp((s) => ({ ...s, host: data.host || '', port: data.port || '587', user: data.user || '', from: data.from || '' }));
      }
    } catch (_) {}
  }, []);

  useEffect(() => { loadHooks(); loadSmtp(); }, [loadHooks, loadSmtp]);

  async function saveSmtp() {
    setSmtpSaving(true);
    setSmtpMsg('');
    try {
      const res = await authFetch('/settings/smtp', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(smtp),
      });
      setSmtpMsg(res.ok ? '✓ Saved' : '✗ Failed');
    } catch (_) {
      setSmtpMsg('✗ Failed');
    } finally {
      setSmtpSaving(false);
      setTimeout(() => setSmtpMsg(''), 3000);
    }
  }

  function openCreate() {
    setEditing(null);
    setForm(emptyForm());
    setModalOpen(true);
  }

  function openEdit(hook) {
    setEditing(hook.id);
    setForm({
      name: hook.name,
      url: hook.url,
      secret: '',
      type: hook.type || 'generic',
      events: hook.events || [],
      minSeverity: hook.minSeverity || 'HIGH',
      repos: hook.repos || [],
      active: hook.active !== false,
    });
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
  }

  async function saveWebhook() {
    if (!form.name.trim() || !form.url.trim()) return;
    setSaving(true);
    try {
      const method = editing ? 'PUT' : 'POST';
      const url = editing ? `/webhooks/${editing}` : '/webhooks';
      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        closeModal();
        loadHooks();
      }
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(hook) {
    await authFetch(`/webhooks/${hook.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...hook, active: !hook.active }),
    });
    loadHooks();
  }

  async function deleteHook(id) {
    await authFetch(`/webhooks/${id}`, { method: 'DELETE' });
    setDeleteConfirm(null);
    loadHooks();
  }

  async function testHook(id) {
    setTestMsg((m) => ({ ...m, [id]: 'sending…' }));
    try {
      const res = await authFetch(`/webhooks/${id}/test`, { method: 'POST' });
      const data = await res.json();
      setTestMsg((m) => ({ ...m, [id]: data.message || 'sent ✓' }));
    } catch (_) {
      setTestMsg((m) => ({ ...m, [id]: 'failed ✗' }));
    }
    setTimeout(() => setTestMsg((m) => { const c = { ...m }; delete c[id]; return c; }), 4000);
  }

  async function loadDeliveries(hookId) {
    if (expandedDeliveries[hookId]) {
      setExpandedDeliveries((d) => ({ ...d, [hookId]: false }));
      return;
    }
    try {
      const res = await authFetch(`/webhooks/${hookId}/deliveries`);
      const data = await res.json();
      setDeliveries((d) => ({ ...d, [hookId]: Array.isArray(data) ? data : [] }));
      setExpandedDeliveries((d) => ({ ...d, [hookId]: true }));
    } catch (_) {}
  }

  function toggleEvent(event) {
    setForm((f) => {
      const events = f.events.includes(event)
        ? f.events.filter((e) => e !== event)
        : [...f.events, event];
      return { ...f, events };
    });
  }

  if (loading) {
    return (
      <div className="App-body">
        <h1 className="ce-page-title">⚡ Webhooks</h1>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="App-body">
      <div className="ce-dashboard-header" style={{ marginBottom: '1.75rem' }}>
        <div>
          <h1 className="ce-page-title">⚡ Webhooks</h1>
          <p className="ce-page-subtitle">
            Push notifications to Slack, Teams, or custom endpoints for DepGuard events.
          </p>
        </div>
        <button className="ce-btn ce-btn--primary ce-btn--sm" onClick={openCreate}>+ Add Webhook</button>
      </div>

      {hooks.length === 0 ? (
        <div className="ce-blank">
          <div className="ce-blank-icon">⚡</div>
          <div className="ce-blank-title">Nog geen webhooks geconfigureerd</div>
          <div className="ce-blank-sub">
            Stel webhooks in om je team automatisch te informeren via Slack, Teams of een eigen endpoint
            wanneer een kritiek CVE gevonden wordt of een policy-gate faalt.
          </div>
          <button className="ce-btn ce-btn--primary mt-3" onClick={openCreate}>Eerste webhook aanmaken</button>
        </div>
      ) : (
        <div className="webhook-list">
          {hooks.map((hook) => (
            <div key={hook.id} className="webhook-card">
              <div className="webhook-card-icon">{typeIcon(hook.type)}</div>
              <div className="webhook-card-body">
                <div className="webhook-card-name">
                  {hook.name}
                  {!hook.active && <span className="badge-inactive" style={{ marginLeft: 8 }}>Inactief</span>}
                </div>
                <div className="webhook-card-url">{hook.url}</div>
                <div className="webhook-card-meta">
                  {(hook.events || []).map((e) => (
                    <span key={e} className="badge-event">{e}</span>
                  ))}
                  {hook.minSeverity && (
                    <span className="badge-event">min: {hook.minSeverity}</span>
                  )}
                  {statusBadge(hook.lastStatus)}
                </div>
                {expandedDeliveries[hook.id] && (
                  <div className="deliveries-panel">
                    <strong style={{ fontSize: '0.8rem' }}>Recente leveringen</strong>
                    {(deliveries[hook.id] || []).length === 0 ? (
                      <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 4 }}>
                        Nog geen leveringen
                      </div>
                    ) : (
                      (deliveries[hook.id] || []).map((d) => (
                        <div key={d.id} className="delivery-row">
                          <span>{d.event}</span>
                          {d.statusCode ? (
                            d.statusCode >= 200 && d.statusCode < 300
                              ? <span className="badge-status-ok">✓ {d.statusCode}</span>
                              : <span className="badge-status-err">✗ {d.statusCode}</span>
                          ) : (
                            <span className="badge-status-err">✗ error</span>
                          )}
                          {d.error && <span style={{ color: 'var(--red)' }}>{d.error}</span>}
                          <span style={{ marginLeft: 'auto' }}>
                            {d.sentAt ? new Date(d.sentAt).toLocaleString('nl-NL') : ''}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              <div className="webhook-card-actions">
                {testMsg[hook.id] && (
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{testMsg[hook.id]}</span>
                )}
                <button className="ce-btn ce-btn--sm ce-btn--secondary" onClick={() => loadDeliveries(hook.id)}>
                  {expandedDeliveries[hook.id] ? '▲' : '📋'} Log
                </button>
                <button className="ce-btn ce-btn--sm ce-btn--secondary" onClick={() => testHook(hook.id)}>
                  🧪 Test
                </button>
                <label className="toggle-switch" title={hook.active ? 'Deactiveren' : 'Activeren'}>
                  <input
                    type="checkbox"
                    checked={hook.active}
                    onChange={() => toggleActive(hook)}
                  />
                  <span className="toggle-slider" />
                </label>
                <button className="ce-btn ce-btn--sm ce-btn--secondary" onClick={() => openEdit(hook)}>✏️</button>
                <button
                  className="ce-btn ce-btn--sm ce-btn--danger"
                  onClick={() => setDeleteConfirm(hook.id)}
                >🗑️</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="webhook-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="webhook-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Webhook verwijderen?</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Deze actie kan niet ongedaan worden gemaakt.
            </p>
            <div className="webhook-modal-actions">
              <button className="ce-btn ce-btn--secondary" onClick={() => setDeleteConfirm(null)}>Annuleren</button>
              <button className="ce-btn ce-btn--danger" onClick={() => deleteHook(deleteConfirm)}>Verwijderen</button>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {modalOpen && (
        <div className="webhook-modal-overlay" onClick={closeModal}>
          <div className="webhook-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editing ? 'Webhook bewerken' : 'Nieuwe webhook'}</h3>

            <div className="webhook-form-group">
              <label>Naam</label>
              <input
                type="text"
                placeholder="bijv. Slack #security-alerts"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>

            <div className="webhook-form-group">
              {form.type === 'email' ? (
                <>
                  <label>Recipient Email</label>
                  <input
                    type="email"
                    placeholder="alerts@example.com"
                    value={form.url}
                    onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                  />
                  <small style={{ color: 'var(--text-muted)', marginTop: 4, display: 'block' }}>
                    Configure SMTP settings below to enable email alerts.
                  </small>
                </>
              ) : (
                <>
                  <label>URL</label>
                  <input
                    type="url"
                    placeholder="https://hooks.slack.com/services/..."
                    value={form.url}
                    onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                  />
                </>
              )}
            </div>

            <div className="webhook-form-group">
              <label>Type</label>
              <select
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
              >
                {TYPE_OPTIONS.map((t) => (
                  <option key={t.value} value={t.value}>{t.label} — {t.desc}</option>
                ))}
              </select>
            </div>

            <div className="webhook-form-group">
              <label>Events</label>
              <div className="events-checkboxes">
                {EVENT_OPTIONS.map((ev) => (
                  <label key={ev.value}>
                    <input
                      type="checkbox"
                      checked={form.events.includes(ev.value)}
                      onChange={() => toggleEvent(ev.value)}
                    />
                    <span><strong>{ev.label}</strong> — {ev.desc}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="webhook-form-group">
              <label>Minimum severity (voor vulnerability events)</label>
              <select
                value={form.minSeverity}
                onChange={(e) => setForm((f) => ({ ...f, minSeverity: e.target.value }))}
              >
                {SEVERITY_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {form.type !== 'email' && (
            <div className="webhook-form-group">
              <label>Secret (optioneel — voor HMAC-SHA256 handtekening)</label>
              <input
                type="password"
                placeholder="Geheim voor handtekening verificatie"
                value={form.secret}
                onChange={(e) => setForm((f) => ({ ...f, secret: e.target.value }))}
              />
            </div>
            )}

            <div className="webhook-form-group" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <label style={{ marginBottom: 0, textTransform: 'none', letterSpacing: 0, fontSize: '0.9rem' }}>
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                  style={{ marginRight: 8 }}
                />
                Webhook actief
              </label>
            </div>

            <div className="webhook-modal-actions">
              <button className="ce-btn ce-btn--secondary" onClick={closeModal}>Annuleren</button>
              <button
                className="ce-btn ce-btn--primary"
                onClick={saveWebhook}
                disabled={saving || !form.name.trim() || !form.url.trim() || form.events.length === 0}
              >
                {saving ? 'Opslaan…' : (editing ? 'Bijwerken' : 'Aanmaken')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SMTP Email Configuration */}
      <div style={{ marginTop: '2.5rem' }}>
        <div className="ce-dashboard-header" style={{ marginBottom: '1rem' }}>
          <div>
            <h2 className="ce-page-title" style={{ fontSize: '1.25rem' }}>📧 SMTP Email Configuration</h2>
            <p className="ce-page-subtitle" style={{ fontSize: '0.875rem' }}>
              Configure an SMTP server to send email alerts for DepGuard events.
            </p>
          </div>
        </div>
        <div className="webhook-card" style={{ display: 'block', padding: '1.25rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="webhook-form-group" style={{ margin: 0 }}>
              <label>SMTP Host</label>
              <input
                type="text"
                placeholder="smtp.example.com"
                value={smtp.host}
                onChange={(e) => setSmtp((s) => ({ ...s, host: e.target.value }))}
              />
            </div>
            <div className="webhook-form-group" style={{ margin: 0 }}>
              <label>SMTP Port</label>
              <input
                type="text"
                placeholder="587"
                value={smtp.port}
                onChange={(e) => setSmtp((s) => ({ ...s, port: e.target.value }))}
              />
            </div>
            <div className="webhook-form-group" style={{ margin: 0 }}>
              <label>Username</label>
              <input
                type="text"
                placeholder="user@example.com"
                value={smtp.user}
                onChange={(e) => setSmtp((s) => ({ ...s, user: e.target.value }))}
              />
            </div>
            <div className="webhook-form-group" style={{ margin: 0 }}>
              <label>Password</label>
              <input
                type="password"
                placeholder="••••••••"
                value={smtp.pass}
                onChange={(e) => setSmtp((s) => ({ ...s, pass: e.target.value }))}
              />
            </div>
            <div className="webhook-form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
              <label>From Address</label>
              <input
                type="email"
                placeholder="depguard@example.com"
                value={smtp.from}
                onChange={(e) => setSmtp((s) => ({ ...s, from: e.target.value }))}
              />
            </div>
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button
              className="ce-btn ce-btn--primary ce-btn--sm"
              onClick={saveSmtp}
              disabled={smtpSaving}
            >
              {smtpSaving ? 'Saving…' : 'Save SMTP Settings'}
            </button>
            {smtpMsg && (
              <span style={{ fontSize: '0.875rem', color: smtpMsg.startsWith('✓') ? 'var(--green, #22c55e)' : 'var(--red, #ef4444)' }}>
                {smtpMsg}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
